// ============================================================================
// PRODUCT SERVICE (Supabase)
// ============================================================================

import { supabase } from './auth.service';
import { Product, ProductExternalMapping } from '../entities/product.entity';
import type { MediaRef } from '../entities/media.entity';

export type InventoryCheckResult = {
  productId: string;
  variant: string;
  inStock: boolean;
  availableStock: number;
  requestedQuantity: number;
  expectedRestockDate?: string;
};

export type AlternativeProductResult = {
  product: Product;
  reason: string;
};

export class ProductService {
  // Get products with filters
  static async getMany(
    filters: {
      categoryId?: string;
      brandId?: string;
      tags?: string[];
      status?: string;
      isVisible?: boolean;
      search?: string;
      limit?: number;
      offset?: number;
      accountId?: string;
    } = {}
  ): Promise<Product[]> {
    let query = supabase
      .from('products')
      .select(
        '*, product_variants(*), categories(*), brands(*), source_integration:account_integrations(*)'
      )
      .order('created_at', { ascending: false });

    if (filters.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    if (filters.brandId) {
      query = query.eq('brand_id', filters.brandId);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.isVisible !== undefined) {
      query = query.eq('is_visible', filters.isVisible);
    }
    if (filters.search) {
      query = query.ilike('label', `%${filters.search}%`);
    }
    if (filters.accountId) {
      query = query.eq('account_id', filters.accountId);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }
    if (filters.offset !== undefined) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  // Get single product with variants
  static async getById(id: string): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .select(
        '*, product_variants(*), categories(*), brands(*), source_integration:account_integrations(*)'
      )
      .eq('id', id)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  // Get multiple products by IDs
  static async getByIds(ids: string[]): Promise<Product[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*, product_variants(*), source_integration:account_integrations(*)')
      .in('id', ids);

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  // Create product
  static async create(product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .insert({
        account_id: product.accountId,
        label: product.label,
        description: product.description,
        slug: product.slug,
        category_id: product.categoryId,
        brand_id: product.brandId,
        tags: product.tags,
        media_data: product.media,
        price: product.price,
        compare_at_price: product.compareAtPrice,
        cost_per_item: product.costPerItem,
        status: product.status,
        is_visible: product.isVisible,
        meta_title: product.metaTitle,
        meta_description: product.metaDescription,
        custom_fields: product.customFields,
      })
      .select(
        '*, product_variants(*), categories(*), brands(*), source_integration:account_integrations(*)'
      )
      .single();

    if (error) throw error;
    const result = this.mapToEntity(data);

    if (product.media?.length) {
      await supabase.from('product_media').delete().eq('product_id', data.id);
      const junctionRows = product.media.map((m: MediaRef, i: number) => ({
        product_id: data.id,
        media_id: m.id,
        account_id: data.account_id,
        sort_order: i,
      }));
      await supabase.from('product_media').insert(junctionRows);
    }

    return result;
  }

  // Update product
  static async update(id: string, updates: Partial<Product>): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .update({
        label: updates.label,
        description: updates.description,
        slug: updates.slug,
        category_id: updates.categoryId,
        brand_id: updates.brandId,
        tags: updates.tags,
        media_data: updates.media,
        price: updates.price,
        compare_at_price: updates.compareAtPrice,
        cost_per_item: updates.costPerItem,
        status: updates.status,
        is_visible: updates.isVisible,
        meta_title: updates.metaTitle,
        meta_description: updates.metaDescription,
        custom_fields: updates.customFields,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select(
        '*, product_variants(*), categories(*), brands(*), source_integration:account_integrations(*)'
      )
      .single();

    if (error) throw error;
    const result = this.mapToEntity(data);

    if (updates.media?.length) {
      await supabase.from('product_media').delete().eq('product_id', id);
      const junctionRows = updates.media.map((m: MediaRef, i: number) => ({
        product_id: id,
        media_id: m.id,
        account_id: data.account_id,
        sort_order: i,
      }));
      await supabase.from('product_media').insert(junctionRows);
    }

    return result;
  }

  // Delete product
  static async delete(id: string): Promise<void> {
    const { error } = await supabase.from('products').delete().eq('id', id);

    if (error) throw error;
  }

  // Find products with filter
  static async find(filter: any): Promise<Product[]> {
    // Convert filter to Supabase query
    return this.getMany(filter);
  }

  // Check stock for variant
  static async checkInventory(
    productId: string,
    quantity: number,
    variantKey?: string
  ): Promise<InventoryCheckResult> {
    let query = supabase.from('product_variants').select('*');

    if (variantKey) {
      // Find variant by variant key (stored in a separate field or through options)
      query = query.eq('product_id', productId).eq('options', variantKey);
    } else {
      // Find default variant
      query = query.eq('product_id', productId).eq('is_default', true);
    }

    const { data, error } = await query.single();

    if (error || !data) {
      return {
        productId,
        variant: variantKey || 'default',
        inStock: false,
        availableStock: 0,
        requestedQuantity: quantity,
      };
    }

    const inStock = (data.stock || 0) >= quantity;
    return {
      productId,
      variant: variantKey || data.label,
      inStock,
      availableStock: data.stock || 0,
      requestedQuantity: quantity,
    };
  }

  // Get low stock products
  static async getLowStockProducts(accountId: string, threshold = 10): Promise<Product[]> {
    const { data, error } = await supabase
      .from('product_variants')
      .select('*, products(*)')
      .lt('stock', threshold)
      .eq('products.account_id', accountId);

    if (error) throw error;

    // Map to products, deduplicating by product ID
    const productMap = new Map<string, Product>();
    for (const item of data || []) {
      if (item.products && !productMap.has(item.products.id)) {
        productMap.set(item.products.id, this.mapToEntity(item.products));
      }
    }

    return Array.from(productMap.values());
  }

  // Get alternative products
  static async getAlternativeProducts(
    productId: string,
    maxPrice?: number,
    limit = 5
  ): Promise<AlternativeProductResult[]> {
    // Get the current product first
    const currentProduct = await this.getById(productId);

    let query = supabase
      .from('products')
      .select('*, product_variants(*)')
      .neq('id', productId)
      .eq('is_visible', true)
      .eq('status', 'ACTIVE');

    if (currentProduct.categoryId) {
      query = query.eq('category_id', currentProduct.categoryId);
    }

    if (maxPrice) {
      // Get the minimum price variant
      query = query;
    }

    query = query.limit(limit);

    const { data, error } = await query;

    if (error) return [];

    return (data || []).map((p) => ({
      product: this.mapToEntity(p),
      reason: 'Similar product',
    }));
  }

  // Update product variant stock
  static async updateVariantStock(variantId: string, stock: number): Promise<void> {
    const { error } = await supabase
      .from('product_variants')
      .update({ stock, updated_at: new Date().toISOString() })
      .eq('id', variantId);

    if (error) throw error;
  }

  // Get external mappings (integrations the product is synced to)
  static async getExternalMappings(productId: string): Promise<ProductExternalMapping[]> {
    const { data, error } = await supabase
      .from('product_external_mappings')
      .select('*, integration:account_integrations(*)')
      .eq('product_id', productId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map((row: any) => ({
      id: row.id,
      integrationId: row.integration_id,
      integrationType: row.integration?.type ?? 'CUSTOM',
      externalId: row.external_id,
      externalData: row.external_data,
      syncedAt: row.synced_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }));
  }

  // Get quick reply templates - stored in database or use defaults
  static async getQuickReplyTemplates(): Promise<Record<string, string>> {
    // Try to fetch from database first
    const { data, error } = await supabase
      .from('quick_reply_templates')
      .select('key, message')
      .eq('is_active', true);

    if (error || !data || data.length === 0) {
      // Return default templates if none in database
      return {
        greeting: 'Hello! Thanks for reaching out. How can I help you today?',
        price_inquiry: 'The price is {price}. Would you like more details?',
        availability: 'Yes, this item is currently in stock!',
        out_of_stock:
          "Sorry, this item is currently out of stock. Would you like to be notified when it's back?",
        shipping: 'We offer shipping within {days} business days. Shipping costs {cost}.',
        thank_you: "Thank you for your order! We'll notify you when it ships.",
        help: "Sure, I'm here to help! What do you need assistance with?",
        promo: 'Great news! We have a special offer: {promo_details}. Would you like to know more?',
        closing: 'Is there anything else I can help you with today?',
        payment:
          'We accept various payment methods including credit cards, PayPal, and bank transfer.',
        order_status: 'Let me check your order status. Please provide your order number.',
      };
    }

    // Convert database rows to key-value map
    const templates: Record<string, string> = {};
    for (const row of data) {
      templates[row.key] = row.message;
    }
    return templates;
  }

  // Map database row to entity
  private static mapToEntity(row: any): Product {
    return {
      id: row.id,
      accountId: row.account_id,
      label: row.label,
      description: row.description,
      slug: row.slug,
      categoryId: row.category_id,
      brandId: row.brand_id,
      brand: row.brands
        ? {
            id: row.brands.id,
            accountId: row.brands.account_id,
            name: row.brands.name,
            description: row.brands.description,
            media: row.brands.media_data || null,
            createdAt: row.brands.created_at,
            updatedAt: row.brands.updated_at,
            createdBy: row.brands.created_by ?? '',
          }
        : null,
      tags: row.tags || [],
      media: row.media_data || [],
      variants:
        row.product_variants?.map((v: any) => ({
          id: v.id,
          productId: v.product_id,
          accountId: v.account_id,
          label: v.label,
          sku: v.sku,
          barcode: v.barcode,
          stock: v.stock,
          weight: v.weight,
          requiresShipping: v.requires_shipping,
          isDefault: v.is_default,
          options: v.options,
          mediaId: v.media_id,
          media: v.media_data || null,
          syncedAt: v.synced_at,
          sourceIntegrationId: v.source_integration_id,
          sourceExternalId: v.source_external_id,
          createdAt: v.created_at,
          updatedAt: v.updated_at,
        })) || [],
      price: row.price,
      compareAtPrice: row.compare_at_price,
      costPerItem: row.cost_per_item,
      status: row.status,
      isVisible: row.is_visible,
      metaTitle: row.meta_title,
      metaDescription: row.meta_description,
      customFields: row.custom_fields,
      syncedAt: row.synced_at,
      sourceIntegrationId: row.source_integration_id,
      sourceIntegrationType: row.source_integration?.type,
      sourceExternalId: row.source_external_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}

// Legacy instance-based class for backward compatibility
export class ProductInstanceService {
  async loadAll(): Promise<Product[]> {
    return ProductService.getMany();
  }

  async loadById(id: string | undefined): Promise<Product> {
    if (!id) throw new Error('Product ID is required');
    return ProductService.getById(id);
  }

  async loadByIds(ids: string[]): Promise<Product[]> {
    return ProductService.getByIds(ids);
  }

  async find(filter: any): Promise<Product[]> {
    return ProductService.find(filter);
  }

  async deleteById(id: string): Promise<void> {
    return ProductService.delete(id);
  }

  async create(product: Product): Promise<Product> {
    return ProductService.create(product);
  }

  async updateById(id: string, product: Product): Promise<Product> {
    return ProductService.update(id, product);
  }

  async checkInventory(
    productId: string,
    quantity: number,
    variant?: string
  ): Promise<InventoryCheckResult> {
    return ProductService.checkInventory(productId, quantity, variant);
  }

  async getAlternativeProducts(
    productId: string,
    maxPrice?: number,
    limit?: number
  ): Promise<AlternativeProductResult[]> {
    return ProductService.getAlternativeProducts(productId, maxPrice, limit);
  }

  async updateRestockDate(
    productId: string,
    expectedRestockDate: string,
    variant?: string
  ): Promise<Product> {
    // For now, just return the product
    return ProductService.getById(productId);
  }

  async getQuickReplyTemplates(): Promise<Record<string, string>> {
    return ProductService.getQuickReplyTemplates();
  }
}

export const productService = new ProductInstanceService();
